import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Dialog,
  Button,
  Stack,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import { useAppTranslation } from "../../hooks/useAppTranslation";
import { getKsefPreviewUrl } from "../../api/documents";
import { useEffect, useMemo, useState, type ReactNode } from "react";

type Props = {
  documentNumber: string;
  documentId: string;
  companyId: string;
  ksefNo?: string | null;
};

type FitMode = "width" | "page" | "cover";

type PreviewResponse = {
  value?: string;
};

type PreviewStateProps = {
  icon: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
};

function PreviewState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: PreviewStateProps) {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 280,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
        background:
          "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 100%)",
      }}
    >
      <Stack
        spacing={1.5}
        alignItems="center"
        sx={{
          maxWidth: 420,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "action.hover",
            color: "text.secondary",
          }}
        >
          {icon}
        </Box>

        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>

        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}

        {actionLabel && onAction && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onAction}
            sx={{ mt: 1, display: "none" }}
          >
            {actionLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function createInvoiceSrcDoc(rawHtml: string, fitMode: FitMode = "width") {
  const parser = new DOMParser();
  const doc = parser.parseFromString(rawHtml, "text/html");

  const originalHead = doc.head?.innerHTML ?? "";
  const originalBody = doc.body?.innerHTML ?? rawHtml;

  return `
<!doctype html>
<html lang="pl">
<head>
  ${originalHead}

  <base target="_blank" />

  <style>
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      width: 100%;
      height: 100%;
      overflow: hidden;
      background: #f3f4f6;
    }

    * {
      box-sizing: border-box;
    }

    #invoice-fit-root {
      width: 100vw;
      height: 100vh;
      overflow: auto;
      background: #f3f4f6;
    }

    #invoice-spacer {
      position: relative;
      margin-left: auto;
      margin-right: auto;
    }

    #invoice-page {
      position: absolute;
      top: 0;
      left: 0;
      width: 794px;
      min-height: 1123px;
      padding: 20px;
      background: #ffffff;
      transform-origin: top left;
      overflow: visible;
    }

    #invoice-page table {
      max-width: 100%;
    }

    #invoice-page img {
      max-width: 100%;
    }

    @media print {
      html,
      body {
        overflow: visible;
        background: white;
      }

      #invoice-fit-root {
        width: auto;
        height: auto;
        overflow: visible;
        background: white;
      }

      #invoice-spacer {
        width: auto !important;
        height: auto !important;
        margin: 0 !important;
      }

      #invoice-page {
        position: static;
        transform: none !important;
        width: auto;
        min-height: auto;
        padding: 0;
        background: white;
      }
    }
  </style>
</head>

<body>
  <div id="invoice-fit-root">
    <div id="invoice-spacer">
      <div id="invoice-page">
        ${originalBody}
      </div>
    </div>
  </div>

  <script>
    (function () {
      const MODE = "${fitMode}";
      const BASE_WIDTH = 794;
      const BASE_HEIGHT = 1123;

      const root = document.getElementById("invoice-fit-root");
      const spacer = document.getElementById("invoice-spacer");
      const page = document.getElementById("invoice-page");

      function fitInvoice() {
        if (!root || !spacer || !page) return;

        page.style.transform = "none";

        const pageWidth = Math.max(
          BASE_WIDTH,
          page.scrollWidth,
          page.offsetWidth
        );

        const pageHeight = Math.max(
          BASE_HEIGHT,
          page.scrollHeight,
          page.offsetHeight
        );

        const scaleX = root.clientWidth / pageWidth;
        const scaleY = root.clientHeight / pageHeight;

        const scale = MODE === "page"
          ? Math.min(scaleX, scaleY)
          : MODE === "cover"
            ? Math.max(scaleX, scaleY)
            : scaleX;

        const scaledWidth = pageWidth * scale;
        const scaledHeight = pageHeight * scale;

        page.style.transform = "scale(" + scale + ")";
        spacer.style.width = scaledWidth + "px";
        spacer.style.height = scaledHeight + "px";

        if (MODE === "page" || MODE === "cover") {
          root.style.overflow = MODE === "cover" ? "hidden" : "hidden";
          spacer.style.marginTop =
            Math.max((root.clientHeight - scaledHeight) / 2, 0) + "px";
        } else {
          root.style.overflowX = "hidden";
          root.style.overflowY = "auto";
          spacer.style.marginTop = "0px";
        }
      }

      window.addEventListener("load", fitInvoice);
      window.addEventListener("resize", fitInvoice);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(fitInvoice);
      }

      if (typeof ResizeObserver !== "undefined") {
        const observer = new ResizeObserver(fitInvoice);

        if (root) observer.observe(root);
        if (page) observer.observe(page);
      }

      setTimeout(fitInvoice, 50);
      setTimeout(fitInvoice, 300);
      setTimeout(fitInvoice, 800);
    })();
  </script>
</body>
</html>
`;
}

export default function DocumentPdfPreview({
  documentNumber,
  documentId,
  companyId,
  ksefNo,
}: Props) {
  const { t } = useAppTranslation("common");

  const previewUrl = useMemo(() => {
    return getKsefPreviewUrl(companyId, documentId);
  }, [companyId, documentId]);

  const [rawPreviewHtml, setRawPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState(false);

  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoaded, setModalLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!ksefNo) {
      setRawPreviewHtml(null);
      setPreviewError(false);
      setLoaded(false);
      setModalLoaded(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    setLoaded(false);
    setModalLoaded(false);
    setPreviewError(false);
    setRawPreviewHtml(null);

    async function loadPreview() {
      try {
        const response = await fetch(previewUrl, {
          signal: controller.signal,
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(`Preview request failed: ${response.status}`);
        }

        const contentType = response.headers.get("content-type") ?? "";
        const responseText = await response.text();

        let html = "";

        if (contentType.includes("application/json")) {
          const data = JSON.parse(responseText) as PreviewResponse;
          html = data.value ?? "";
        } else {
          try {
            const data = JSON.parse(responseText) as PreviewResponse;
            html = data.value ?? responseText;
          } catch {
            html = responseText;
          }
        }

        if (!html.trim()) {
          throw new Error("Preview HTML is empty");
        }

        if (!cancelled) {
          setRawPreviewHtml(html);
        }
      } catch (error) {
        if (!cancelled && (error as Error).name !== "AbortError") {
          setPreviewError(true);
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [previewUrl, ksefNo, reloadKey]);

  const previewSrcDoc = useMemo(() => {
    if (!rawPreviewHtml) return "";

    // Mały podgląd: dopasowanie do szerokości kontenera + scroll pionowy.
    // Jeśli chcesz zmieścić całą stronę bez scrolla, zmień "width" na "page".
    // Jeśli chcesz wypełnić cały kontener kosztem możliwego przycięcia, zmień "width" na "cover".
    return createInvoiceSrcDoc(rawPreviewHtml, "width");
  }, [rawPreviewHtml]);

  const modalSrcDoc = useMemo(() => {
    if (!rawPreviewHtml) return "";

    // Modal: czytelny widok dopasowany do szerokości.
    return createInvoiceSrcDoc(rawPreviewHtml, "width");
  }, [rawPreviewHtml]);

  const openModal = () => {
    setModalLoaded(false);
    setModalOpen(true);
  };

  const retryPreviewLoad = () => {
    setReloadKey((current) => current + 1);
  };

  if (!ksefNo) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t("Podgląd dokumentu")}
        </Typography>

        <Box
          sx={{
            height: "70vh",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "background.default",
          }}
        >
          <PreviewState
            icon={<DescriptionOutlinedIcon sx={{ fontSize: 34 }} />}
            title={t("Podgląd dokumentu nie jest dostępny")}
            description={t(
              "Ten dokument nie posiada numeru KSeF, dlatego nie można wyświetlić wizualizacji faktury."
            )}
          />
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="subtitle1">
            {t("Podgląd dokumentu")}
          </Typography>

          <IconButton size="small" onClick={openModal}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            height: "70vh",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            position: "relative",
            backgroundColor: "#fff",
          }}
        >
          {rawPreviewHtml && !previewError && (
            <iframe
              srcDoc={previewSrcDoc}
              onLoad={() => setLoaded(true)}
              title="KSeF Preview"
              sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                display: "block",
              }}
            />
          )}

          {(!loaded || !rawPreviewHtml) && !previewError && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1,
                backgroundColor: "rgba(255,255,255,0.7)",
                backdropFilter: "blur(2px)",
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                {t("Ładowanie podglądu...")}
              </Typography>
            </Box>
          )}

          {previewError && (
            <PreviewState
              icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: 34 }} />}
              title={t("Nie udało się pobrać podglądu")}
              description={t(
                "Wystąpił problem podczas pobierania wizualizacji faktury. Sprawdź połączenie lub spróbuj ponownie za chwilę."
              )}
              actionLabel={t("Spróbuj ponownie")}
              onAction={retryPreviewLoad}
            />
          )}
        </Box>
      </Paper>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "90vh",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle1">
              {t("Podgląd dokumentu")} – {documentNumber}
            </Typography>

            <IconButton onClick={() => setModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              position: "relative",
              backgroundColor: "#fff",
            }}
          >
            {rawPreviewHtml && !previewError && (
              <iframe
                srcDoc={modalSrcDoc}
                onLoad={() => setModalLoaded(true)}
                title="KSeF Preview Modal"
                sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  display: "block",
                }}
              />
            )}

            {(!modalLoaded || !rawPreviewHtml) && !previewError && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1,
                  backgroundColor: "rgba(255,255,255,0.7)",
                  backdropFilter: "blur(2px)",
                }}
              >
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  {t("Ładowanie podglądu...")}
                </Typography>
              </Box>
            )}

            {previewError && (
              <PreviewState
                icon={<ErrorOutlineOutlinedIcon sx={{ fontSize: 34 }} />}
                title={t("Nie udało się pobrać podglądu")}
                description={t(
                  "Wystąpił problem podczas pobierania wizualizacji faktury. Sprawdź połączenie lub spróbuj ponownie za chwilę."
                )}
                actionLabel={t("Spróbuj ponownie")}
                onAction={retryPreviewLoad}
              />
            )}
          </Box>
        </Box>
      </Dialog>
    </>
  );
}